import Typography from "@material-ui/core/Typography"
import Translation from "components/Translation"
import React, { memo, useMemo, useState } from "react"
import Grid from "@material-ui/core/Grid"
import { PaperProps } from "@material-ui/core"
import Button from "components/Button"
import MuiDialog from "@material-ui/core/Dialog/Dialog"
import Close from "components/Icons/Close.icon"
import LoadingIndicator from "components/LoadingIndicator"
import {
  DialogActions,
  DialogBody,
  DialogCaption,
  DialogCloseButton,
  DialogCloseIcon,
  DialogHeader,
  DialogHeaderContent,
  StyledDialogPaper,
  StyledBackdrop,
} from "./style"
import { IDialogProps } from "./types"

function Dialog({
  heading,
  icon,
  caption,
  footer,
  actions,
  open,
  loading,
  onClose,
  width,
  maxWidth = "sm",
  contentHeight,
  disableBackdropClick,
  hasConfirmClose,
  renderConfirmCloseDialog,
  children,
  paperProps,
}: IDialogProps): JSX.Element {
  const [isConfirmed, setIsConfirmed] = useState(false)
  const PaperComponent = useMemo(
    () => (props: PaperProps) => <StyledDialogPaper width={width} {...props} {...paperProps} />,
    [width, paperProps],
  )

  function toggleConfirmation() {
    setIsConfirmed(!isConfirmed)
  }

  const checkOnCloseHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (hasConfirmClose) {
      setIsConfirmed(true)
    } else {
      setIsConfirmed(false)
      onClose(event)
    }
  }

  return (
    <>
      <MuiDialog
        open={open}
        onClose={checkOnCloseHandler}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperComponent={PaperComponent}
        maxWidth={maxWidth}
        disableBackdropClick={disableBackdropClick}
      >
        {loading && (
          <StyledBackdrop open={true}>
            <LoadingIndicator />
          </StyledBackdrop>
        )}
        <DialogHeader>
          <DialogHeaderContent>
            {icon && icon}
            {heading && (
              <Typography component="h3" variant="h3">
                {typeof heading === "string" ? <Translation label={heading} /> : heading}
              </Typography>
            )}
            {caption && (
              <DialogCaption>
                <Typography color="inherit">
                  <Translation label={caption} />
                </Typography>
              </DialogCaption>
            )}
          </DialogHeaderContent>
          <DialogCloseButton data-cy="dialog-close-action" onClick={checkOnCloseHandler}>
            <DialogCloseIcon component={Close} />
          </DialogCloseButton>
        </DialogHeader>
        <DialogBody height={contentHeight}>
          <>
            {children}
            {renderConfirmCloseDialog?.({ onConfirm: onClose, open: isConfirmed, onClose: toggleConfirmation })}
          </>
        </DialogBody>
        {(actions || footer) && (
          <DialogActions>
            {actions ? (
              <Grid container={true} spacing={2} justifyContent="flex-end">
                {actions.map(({ onClick, label, cySelector, formId, ...rest }) => (
                  <Grid item={true} key={label}>
                    <Button onClick={onClick} type="submit" form={formId} data-cy={cySelector} {...rest}>
                      <Translation label={label} />
                    </Button>
                  </Grid>
                ))}
              </Grid>
            ) : (
              footer
            )}
          </DialogActions>
        )}
      </MuiDialog>
    </>
  )
}

export default memo(Dialog)
